import { useState, useEffect, useCallback } from 'react'
import useRouter from 'utils/useRouter'
import { useCadaverService } from 'hooks/useCadaverService'

export const useFormEntrega = (props) => {
  const { cadaver } = props
  const router = useRouter()
  const cadaverService = useCadaverService()

  const [activeStep, setActiveStep] = useState(0)
  const [processando, setProcessando] = useState(false)
  const [openEspelho, setOpenEspelho] = useState(false)
  const [dadosFormatados, setDadosFormatados] = useState({})

  // FormState do Recebedor (Passo 0)
  const [recebedorFormState, setRecebedorFormState] = useState({
    values: {
      nome: '',
      tipoDocumento: '',
      numeroDocumento: '',
      orgaoEmissorDocumento: '',
      telefone: '',
      grauRelacao: '',
      arquivos: [],
    },
    errors: {},
    touched: {},
  })

  // FormState da Conclusão de Entrega (Passo 1)
  const [conclusaoFormState, setConclusaoFormState] = useState({
    values: {
      declaracaoObito: '',
      cemiterio: '',
      localizacaoInternaCemiterio: '',
      pertences: '',
      entregaObservacoes: '',
      endereco: '',
      etnia: '',
      estadoCivil: '',
      escolaridade: '',
      profissao: '',
    },
    errors: {},
    touched: {},
  })

  // Sincroniza dados iniciais do recebedor com o reconhecedor anterior (se existir no cadáver)
  useEffect(() => {
    if (cadaver && cadaver.reconhec) {
      setRecebedorFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          nome: cadaver.reconhec.nome || '',
          tipoDocumento: cadaver.reconhec.tipoDocumento || '',
          numeroDocumento: cadaver.reconhec.numeroDocumento || '',
          orgaoEmissorDocumento: cadaver.reconhec.orgaoEmissorDocumento || '',
          telefone: cadaver.reconhec.telefone || '',
          grauRelacao: cadaver.reconhec.grauRelacao || '',
        },
      }))
    }
  }, [cadaver])

  // Validação do Recebedor (Etapa 0)
  useEffect(() => {
    const errors = {}
    if (!recebedorFormState.values.nome || !recebedorFormState.values.nome.trim()) {
      errors.nome = ['Nome é obrigatório']
    }
    if (!recebedorFormState.values.tipoDocumento) {
      errors.tipoDocumento = ['Tipo do documento é obrigatório']
    }
    if (!recebedorFormState.values.numeroDocumento || !recebedorFormState.values.numeroDocumento.trim()) {
      errors.numeroDocumento = ['Número do documento é obrigatório']
    }
    if (!recebedorFormState.values.orgaoEmissorDocumento || !recebedorFormState.values.orgaoEmissorDocumento.trim()) {
      errors.orgaoEmissorDocumento = ['Órgão emissor é obrigatório']
    }
    if (!recebedorFormState.values.telefone || !recebedorFormState.values.telefone.trim()) {
      errors.telefone = ['Telefone é obrigatório']
    }
    if (!recebedorFormState.values.grauRelacao) {
      errors.grauRelacao = ['Grau de relação é obrigatório']
    }
    setRecebedorFormState((prev) => ({ ...prev, errors }))
  }, [
    recebedorFormState.values.nome,
    recebedorFormState.values.tipoDocumento,
    recebedorFormState.values.numeroDocumento,
    recebedorFormState.values.orgaoEmissorDocumento,
    recebedorFormState.values.telefone,
    recebedorFormState.values.grauRelacao,
  ])

  // Validação da Conclusão (Etapa 1)
  useEffect(() => {
    const errors = {}
    if (!conclusaoFormState.values.declaracaoObito || !conclusaoFormState.values.declaracaoObito.trim()) {
      errors.declaracaoObito = ['Número da declaração de óbito é obrigatório']
    }
    if (!conclusaoFormState.values.cemiterio || !conclusaoFormState.values.cemiterio.trim()) {
      errors.cemiterio = ['Cemitério é obrigatório']
    }
    if (!conclusaoFormState.values.localizacaoInternaCemiterio || !conclusaoFormState.values.localizacaoInternaCemiterio.trim()) {
      errors.localizacaoInternaCemiterio = ['Localização interna do cemitério é obrigatória']
    }
    if (!conclusaoFormState.values.endereco || !conclusaoFormState.values.endereco.trim()) {
      errors.endereco = ['Endereço é obrigatório']
    }
    if (!conclusaoFormState.values.etnia) {
      errors.etnia = ['Etnia é obrigatória']
    }
    if (!conclusaoFormState.values.estadoCivil) {
      errors.estadoCivil = ['Estado civil é obrigatório']
    }
    if (!conclusaoFormState.values.escolaridade) {
      errors.escolaridade = ['Escolaridade é obrigatória']
    }
    if (!conclusaoFormState.values.profissao || !conclusaoFormState.values.profissao.trim()) {
      errors.profissao = ['Profissão é obrigatória']
    }
    setConclusaoFormState((prev) => ({ ...prev, errors }))
  }, [
    conclusaoFormState.values.declaracaoObito,
    conclusaoFormState.values.cemiterio,
    conclusaoFormState.values.localizacaoInternaCemiterio,
    conclusaoFormState.values.endereco,
    conclusaoFormState.values.etnia,
    conclusaoFormState.values.estadoCivil,
    conclusaoFormState.values.escolaridade,
    conclusaoFormState.values.profissao,
  ])

  // Emulador de FormState para o componente FormPessoaReconhecimento
  const recebedorForm = {
    formState: recebedorFormState,
    setFormState: setRecebedorFormState,
    handleChange: useCallback((event) => {
      const { name, value } = event.target
      setRecebedorFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    handleChangeNewValueByNameProperty: useCallback((event, newValue, name) => {
      setRecebedorFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: newValue },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    hasError: useCallback((name) => Boolean(recebedorFormState.errors[name]), [recebedorFormState.errors]),
  }

  // Emulador de FormState para o componente FormConclusaoEntrega
  const conclusaoForm = {
    formState: conclusaoFormState,
    setFormState: setConclusaoFormState,
    handleChange: useCallback((event) => {
      const { name, value } = event.target
      setConclusaoFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),

    handleChangeNewValueByNameProperty: useCallback((event, newValue, name) => {
      setConclusaoFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: newValue },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    hasError: useCallback((name) => Boolean(conclusaoFormState.errors[name]), [conclusaoFormState.errors]),
  }

  const formError = () => {
    if (activeStep === 0) {
      return Boolean(
        recebedorFormState.errors.nome ||
        recebedorFormState.errors.tipoDocumento ||
        recebedorFormState.errors.numeroDocumento ||
        recebedorFormState.errors.orgaoEmissorDocumento ||
        recebedorFormState.errors.telefone ||
        recebedorFormState.errors.grauRelacao
      )
    }
    if (activeStep === 1) {
      return Boolean(
        conclusaoFormState.errors.declaracaoObito ||
        conclusaoFormState.errors.cemiterio ||
        conclusaoFormState.errors.localizacaoInternaCemiterio ||
        conclusaoFormState.errors.endereco ||
        conclusaoFormState.errors.etnia ||
        conclusaoFormState.errors.estadoCivil ||
        conclusaoFormState.errors.escolaridade ||
        conclusaoFormState.errors.profissao
      )
    }
    return false
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleNext = () => {
    if (activeStep === 2) {
      // Finalizar Entrega e estruturar payload
      const dados = {
        recebedor: {
          ...recebedorFormState.values,
        },
        cadaver: {
          id: cadaver.id,
          ...conclusaoFormState.values,
        },
      }
      setDadosFormatados(dados)
      setOpenEspelho(true)
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleConfirmar = async () => {
    await cadaverService.entregarCadaver(dadosFormatados, setProcessando)
    router.history.push(`/casos/caso/cadaver/detalhar/${cadaver.id}`)
  }

  return {
    activeStep,
    steps: ['Recebedor do cadáver', 'Conclusão da entrega', 'Documentos Relacionados'],
    recebedorForm,
    conclusaoForm,
    processando,
    openEspelho,
    setOpenEspelho,
    dadosFormatados,
    formError,
    handleBack,
    handleNext,
    handleConfirmar,
    router,
  }
}
